/**
 * Impressoras térmicas (ESC/POS) e balanças de caixa — só os bytes e o texto,
 * sem I/O (a ligação USB/série/Bluetooth é da aplicação).
 *
 * Os talões não se reescrevem em comandos de texto: a aplicação desenha o
 * mesmo HTML que já imprime (talão, Z, cozinha) numa imagem a 1 bit e envia-a
 * em raster (GS v 0) — acentos, tamanhos e o aspecto ficam iguais em qualquer
 * impressora, sem tabelas de caracteres.
 */
/** Pontos de largura útil por papel (203 ppp): 80 mm → 576, 58 mm → 384. */
export const PRINT_DOTS = { 80: 576, 58: 384 };
const ESC = 0x1b, GS = 0x1d;
/** Repõe a impressora (modo e tabela por omissão). */
export const ESCPOS_INIT = Uint8Array.of(ESC, 0x40);
/** Avança o papel e corta (corte parcial; as que não cortam ignoram). */
export const ESCPOS_CUT = Uint8Array.of(ESC, 0x64, 4, GS, 0x56, 0x42, 0);
/** Impulso da gaveta (pino 2, 100 ms ligado / 200 ms desligado) — a gaveta liga-se à impressora. */
export const ESCPOS_DRAWER = Uint8Array.of(ESC, 0x70, 0, 50, 100);
export function concatBytes(parts) {
    const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let o = 0;
    for (const p of parts) {
        out.set(p, o);
        o += p.length;
    }
    return out;
}
/**
 * RGBA (de um canvas) → 1 bit por ponto, linha a linha, MSB à esquerda;
 * preto quando a luminância fica abaixo do limiar (o transparente é branco).
 * A largura é arredondada para múltiplos de 8 (colunas a mais ficam brancas).
 */
export function toMonochrome(rgba, width, height, threshold = 160) {
    const bytesPerRow = Math.ceil(width / 8);
    const bits = new Uint8Array(bytesPerRow * height);
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            const a = rgba[i + 3] / 255;
            const lum = (0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2]) * a + 255 * (1 - a);
            if (lum < threshold)
                bits[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7);
        }
    }
    return { bits, bytesPerRow, height };
}
/** Corta o branco do fim (o HTML mede mais do que o conteúdo): menos papel e menos bytes. */
export function trimBottom(img, keep = 8) {
    let last = img.height - 1;
    while (last >= 0 && img.bits.subarray(last * img.bytesPerRow, (last + 1) * img.bytesPerRow).every((b) => b === 0))
        last--;
    const height = Math.min(img.height, last + 1 + keep);
    return { bits: img.bits.slice(0, height * img.bytesPerRow), bytesPerRow: img.bytesPerRow, height };
}
/**
 * Imagem em raster (GS v 0), em faixas de até `band` linhas: várias
 * impressoras baratas têm memória curta e perdem imagens longas de uma vez.
 */
export function escposRaster(img, band = 128) {
    const parts = [];
    for (let y = 0; y < img.height; y += band) {
        const h = Math.min(band, img.height - y);
        parts.push(Uint8Array.of(GS, 0x76, 0x30, 0, img.bytesPerRow & 0xff, img.bytesPerRow >> 8, h & 0xff, h >> 8));
        parts.push(img.bits.subarray(y * img.bytesPerRow, (y + h) * img.bytesPerRow));
    }
    return concatBytes(parts);
}
/** Trabalho completo: repor, imagem, cortar e (opcional) abrir a gaveta. */
export function escposJob(img, opts = {}) {
    return concatBytes([
        ESCPOS_INIT,
        ...(img ? [escposRaster(img)] : []),
        ...(img && opts.cut !== false ? [ESCPOS_CUT] : []),
        ...(opts.drawer ? [ESCPOS_DRAWER] : []),
    ]);
}
export const SCALE_REQUEST = { stream: null, toledo: Uint8Array.of(0x57), enq: Uint8Array.of(0x05) };
/**
 * Lê uma resposta de balança (texto ASCII): a última leitura completa.
 * - CAS e semelhantes: "ST,GS,+  1.234kg" (ST estável, US instável; OL sobrecarga).
 * - Toledo 8217: STX "01.234" CR; "?" + estado quando instável ou em movimento.
 * - Genérico: o último número com decimais, em kg (ou g, convertido).
 * Peso zero ou negativo, ou sobrecarga, não é leitura (null).
 */
export function parseScaleReading(raw) {
    const frames = raw.replace(/\x02/g, '\n').split(/[\r\n]+/).map((s) => s.trim()).filter(Boolean);
    for (let i = frames.length - 1; i >= 0; i--) {
        const f = frames[i];
        if (/^OL\b|,OL,|overload/i.test(f))
            return null;
        if (f.startsWith('?'))
            return null; // Toledo: em movimento ou erro
        const m = f.match(/([-+]?)\s*(\d+(?:[.,]\d+)?)\s*(kg|g)?\s*$/i);
        if (!m)
            continue;
        if (m[1] === '-')
            return null;
        let n = Number(m[2].replace(',', '.'));
        if (/^g$/i.test(m[3] ?? ''))
            n /= 1000;
        else if (!m[3] && !/[.,]/.test(m[2]))
            continue; // número solto sem unidade nem decimais: não é peso
        if (!(n > 0))
            return null;
        const stable = !/^US\b|,US,|^U\b/i.test(f);
        return { kg: n.toFixed(3).replace(/\.?0+$/, '') || '0', stable };
    }
    return null;
}
//# sourceMappingURL=escpos.js.map